import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PolicyStatusPill from './PolicyStatusPill';
import NotificationBanner from './NotificationBanner';
import { formatPolicyNumber, formatPolicyDate } from '../../utils/policyDisplay';

// reuses only the app-wide `.modal` overlay for the backdrop and stacking behaviour consistency
const PolicyDetailModal = ({
    policy,
    onClose,
    onDownload,
    isDownloading,
    downloadError,
    onDismissDownloadError
}) => {
    const { t } = useTranslation();

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === 'Escape') onClose();
        };

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose]);

    const handleOverlayClick = (event) => {
        if (event.target === event.currentTarget) onClose();
    };

    return (
        <div className="modal open" onClick={handleOverlayClick}>
            <div className="policy-modal" role="dialog" aria-modal="true">
                <div className="policy-modal__header">
                    <h3 className="policy-modal__title">
                        {t('policies.detailModalTitle', {
                            policyNumber: formatPolicyNumber(policy.reference_code)
                        })}
                    </h3>
                    <button
                        type="button"
                        className="policy-modal__close"
                        aria-label={t('policies.detailModalCloseLabel')}
                        onClick={onClose}
                    >
                        <HighlightOffIcon />
                    </button>
                </div>

                <div className="policy-modal__status">
                    <PolicyStatusPill status={policy.policy_status} />
                </div>

                <div className="policy-modal__dates">
                    <div>
                        <p className="policy-modal__label">
                            {t('policies.dataTableStartDateColumnAlias')}
                        </p>
                        <p className="policy-modal__value">
                            {formatPolicyDate(t, policy.start_date)}
                        </p>
                    </div>
                    <div>
                        <p className="policy-modal__label">
                            {t('policies.dataTableEndDateColumnAlias')}
                        </p>
                        <p className="policy-modal__value">
                            {formatPolicyDate(t, policy.end_date)}
                        </p>
                    </div>
                </div>

                <hr className="policy-modal__divider" />

                <div className="policy-modal__certificate">
                    <div className="policy-modal__certificate-info">
                        <span className="policy-modal__pdf-badge">PDF</span>
                        <div>
                            <p className="policy-modal__certificate-title">
                                {t('policies.certificateCardTitle')}
                            </p>
                            <p className="policy-modal__certificate-subtitle">
                                {t('policies.certificateCardSubtitle')}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="policy-modal__download"
                        disabled={isDownloading}
                        onClick={() => onDownload(policy)}
                    >
                        <FileDownloadOutlinedIcon />
                        {isDownloading
                            ? t('policies.downloadingLabel')
                            : t('policies.downloadButtonLabel')}
                    </button>
                </div>

                {downloadError && (
                    <NotificationBanner
                        title={t('common.errorNotificationTitle', 'Error')}
                        message={downloadError}
                        type="error"
                        close={onDismissDownloadError}
                    />
                )}
            </div>
        </div>
    );
};

PolicyDetailModal.propTypes = {
    policy: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        reference_code: PropTypes.string,
        start_date: PropTypes.string,
        end_date: PropTypes.string,
        policy_status: PropTypes.string
    }).isRequired,
    onClose: PropTypes.func.isRequired,
    onDownload: PropTypes.func.isRequired,
    isDownloading: PropTypes.bool,
    downloadError: PropTypes.string,
    onDismissDownloadError: PropTypes.func
};

export default PolicyDetailModal;
